import { AppBar, IconButton, Stack, Toolbar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { roleCanSeePortalNotifications } from '../../../assets/data';
import { useAuthContext } from '../../../auth/useAuthContext';
import Iconify from '../../../components/iconify';
import LoadingScreen from '../../../components/loading-screen/LoadingScreen';
import Logo from '../../../components/logo';
import { useSettingsContext } from '../../../components/settings';
import { HEADER, NAV } from '../../../config-global';
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
import PortalNotificationBanner from '../../../portalNotification/components/PortalNotificationBanner';
import { usePortalNotificationContext } from '../../../portalNotification/context/PortalNotificationProvider';
import { bgBlur } from '../../../utils/cssStyles';
import { hasSessionAdmin } from '../../../utils/mrr/emulator/signInAsOtherUser';
import AccountPopover from './AccountPopover';
import PortalNotificationsPopover from './NotificationsPopover';
import ReturnToAdminPopover from './ReturnToAdminPopover';
import Searchbar from './Searchbar';
// import LanguagePopover from './LanguagePopover';
// import ContactsPopover from './ContactsPopover';

// ----------------------------------------------------------------------

type Props = {
  showNotifications: boolean,
  onOpenNav?: VoidFunction;
};

export default function Header({ onOpenNav, showNotifications }: Props) {
  const theme = useTheme();
  const { user } = useAuthContext()
  const isOwner = roleCanSeePortalNotifications(user?.role)

  const noticeControls = usePortalNotificationContext()
  const { childrenTopMargin } = noticeControls
  const notificationMargin = showNotifications
    ? childrenTopMargin
    : 0

  const { themeLayout } = useSettingsContext();

  const isNavHorizontal = themeLayout === 'horizontal';

  const isNavMini = themeLayout === 'mini';

  const isDesktop = useResponsive('up', 'lg');

  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;

  const [loggingOutOfOtherUser] = useState(false)

  const renderContent = (
    <>
      {isDesktop && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {!isDesktop && (
        <IconButton onClick={onOpenNav} sx={{ mr: 1, color: 'text.primary' }}>
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
      )}

      <Searchbar />

      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 1, sm: 1.5 }}
      // spacing={{ xs: 0.5, sm: 1.5 }}
      >
        {/* <LanguagePopover /> */}

        {hasSessionAdmin() && <ReturnToAdminPopover />}

        {isOwner && <PortalNotificationsPopover />}

        {/* <ContactsPopover /> */}

        <AccountPopover />
      </Stack>
    </>
  );

  if (loggingOutOfOtherUser) {
    return <LoadingScreen />
  }

  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        height: HEADER.H_MOBILE + notificationMargin,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
          height: HEADER.H_DASHBOARD_DESKTOP + notificationMargin,
          ...(isOffset && {
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET + notificationMargin,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET + notificationMargin,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Stack>
        <Toolbar
          sx={{
            height: 1,
            px: { lg: 5 },
          }}
        >
          {renderContent}
        </Toolbar>
        {showNotifications
          && isOwner
          && <PortalNotificationBanner />}
      </Stack>
    </AppBar>
  );
}
